import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { auditTrail } from '@/mocks/systemData';

export default function AdminDataSecurityPage() {
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupDone, setBackupDone] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [accessFilter, setAccessFilter] = useState('all');

  const handleBackup = () => {
    setBackupRunning(true);
    setTimeout(() => { setBackupRunning(false); setBackupDone(true); setTimeout(() => setBackupDone(false), 3000); }, 2000);
  };

  const filteredLogs = auditTrail.filter((a) => accessFilter === 'all' || a.status === accessFilter);

  return (
    <AppLayout title="Data Security" breadcrumb={['Admin', 'Data Security']}>
      <div className="space-y-6">
        {/* Security Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Security Alerts', value: auditTrail.filter((a) => a.status === 'failed').length, icon: 'ri-alarm-warning-line', color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Warnings', value: auditTrail.filter((a) => a.status === 'warning').length, icon: 'ri-error-warning-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Successful Logins', value: auditTrail.filter((a) => a.status === 'success' && a.action.includes('Login')).length, icon: 'ri-login-circle-line', color: 'text-teal-600', bg: 'bg-teal-50' },
            { label: 'Last Backup', value: '12h ago', icon: 'ri-database-2-line', color: 'text-gray-600', bg: 'bg-gray-50' },
          ].map((k) => (
            <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-5">
              <div className={`w-10 h-10 ${k.bg} rounded-xl flex items-center justify-center mb-3`}>
                <i className={`${k.icon} ${k.color} text-lg`} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{k.value}</p>
              <p className="text-sm text-gray-500 mt-1">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Backup & Restore */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><i className="ri-database-2-line text-teal-500" />Backup &amp; Restore</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-sm font-medium text-gray-700 mb-1">Last Backup</p>
              <p className="text-lg font-bold text-gray-900">2026-04-09 12:00</p>
              <p className="text-xs text-gray-400 mt-1">Size: 24.5 MB · Status: Success</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-sm font-medium text-gray-700 mb-1">Backup Schedule</p>
              <p className="text-lg font-bold text-gray-900">Daily at 12:00 AM</p>
              <p className="text-xs text-gray-400 mt-1">Retention: 30 days · Location: Secure Cloud</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={handleBackup} disabled={backupRunning} className="bg-teal-500 text-white text-sm px-5 py-2.5 rounded-xl hover:bg-teal-600 disabled:bg-teal-300 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              {backupRunning ? <><i className="ri-loader-4-line animate-spin" />Running...</> : <><i className="ri-save-line" />Run Manual Backup</>}
            </button>
            {backupDone && <span className="text-sm text-teal-600 bg-teal-50 border border-teal-200 px-3 py-2 rounded-xl flex items-center gap-1"><i className="ri-checkbox-circle-line" />Backup completed!</span>}
            <button onClick={() => setRestoreConfirm(true)} className="border border-gray-200 text-gray-700 text-sm px-5 py-2.5 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-refresh-line" />Restore from Backup</button>
            <button className="border border-gray-200 text-gray-700 text-sm px-5 py-2.5 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-2"><i className="ri-download-line" />Download Backup</button>
          </div>
        </div>

        {/* Access Logs */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2"><i className="ri-eye-line text-teal-500" />Access Logs</h3>
            <div className="flex gap-2">
              {['all', 'success', 'warning', 'failed'].map((f) => (
                <button key={f} onClick={() => setAccessFilter(f)} className={`text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap capitalize ${accessFilter === f ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f}</button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3 text-left font-medium">Timestamp</th>
                  <th className="px-5 py-3 text-left font-medium">User</th>
                  <th className="px-5 py-3 text-left font-medium">Action</th>
                  <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Module</th>
                  <th className="px-5 py-3 text-left font-medium hidden lg:table-cell">IP</th>
                  <th className="px-5 py-3 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredLogs.map((a) => (
                  <tr key={a.id} className={`hover:bg-gray-50 transition-colors ${a.status === 'failed' ? 'bg-red-50/30' : ''}`}>
                    <td className="px-5 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">{a.timestamp}</td>
                    <td className="px-5 py-3"><p className="text-sm font-medium text-gray-800">{a.user}</p><p className="text-xs text-gray-400">{a.role}</p></td>
                    <td className="px-5 py-3"><p className="text-sm text-gray-700">{a.action}</p><p className="text-xs text-gray-400 truncate max-w-48">{a.details}</p></td>
                    <td className="px-5 py-3 hidden md:table-cell text-xs text-gray-500">{a.module}</td>
                    <td className="px-5 py-3 hidden lg:table-cell text-xs font-mono text-gray-400">{a.ip}</td>
                    <td className="px-5 py-3"><span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${a.status === 'success' ? 'bg-green-100 text-green-700' : a.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{a.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Restore Confirm Modal */}
      {restoreConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"><i className="ri-refresh-line text-amber-500 text-2xl" /></div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Restore from Backup?</h3>
            <p className="text-sm text-gray-500 mb-5">This will restore the system to the last backup state. Current unsaved changes will be lost.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setRestoreConfirm(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancel</button>
              <button onClick={() => setRestoreConfirm(false)} className="text-sm bg-amber-500 text-white px-4 py-2 rounded-xl hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">Restore</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
